import { useCallback, useEffect, useState } from "react";
import { adminService } from "../../features/admin/service";
import type { WebsocketConnectionsResponse, AllRedisPoolSizesResponse, RedisPoolInfo } from "../../features/admin/types";
import Spinner from "../../components/ui/Spinner";
import RefreshButton from "../../components/admin/RefreshButton";
import { RealtimePanel, RedisPanel, QueuePanel } from "./AdminRealtimeOpsPanels";

const REFRESH_INTERVAL_MS = 5000;

const AdminWebSocketHealthPage: React.FC = () => {
  const [data, setData] = useState<WebsocketConnectionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const [pools, setPools] = useState<AllRedisPoolSizesResponse | null>(null);
  const [poolInputs, setPoolInputs] = useState<Record<string, string>>({});
  const [poolSaving, setPoolSaving] = useState<Record<string, boolean>>({});
  const [poolErrors, setPoolErrors] = useState<Record<string, string>>({});

  // rec_engine's Redis-server-wide maxclients cap — distinct from the pool
  // sizes above (those are how many connections THIS app opens; this is how
  // many the Redis server will accept from everyone combined).
  const [maxclientsInput, setMaxclientsInput] = useState<string>("");
  const [, setMaxclientsSeeded] = useState(false);
  const [maxclientsSaving, setMaxclientsSaving] = useState(false);
  const [maxclientsError, setMaxclientsError] = useState("");

  type Tab = "connections" | "realtime" | "redis" | "queues";
  const [tab, setTab] = useState<Tab>("connections");

  const fetchConnections = useCallback(async () => {
    try {
      const resp = await adminService.getWebsocketConnections();
      setData(resp);
      setLastUpdated(new Date());
      setError(null);
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.detail || "Failed to load connection health");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPools = useCallback(async () => {
    try {
      const resp = await adminService.getAllRedisPoolSizes();
      setPools(resp);
      // Don't stomp on an in-progress edit — only seed each input the first
      // time its value is seen, or right after that row's own save.
      setPoolInputs((prev) => {
        const next = { ...prev };
        for (const [service, info] of Object.entries(resp)) {
          if (service === "_summary" || typeof info.default !== "number") continue;
          // A service that hasn't connected yet (lazy-init not triggered)
          // has no `current` — fall back to `default` so the input still
          // shows a sensible number to edit/confirm instead of staying
          // blank. A blank input silently fails client-side validation on
          // Save ("Enter a positive number") with no request ever sent,
          // which reads as "Save is broken" for exactly the RAG/S3 rows
          // that show "Not yet connected".
          if (next[service] === undefined) {
            next[service] = String(info.current ?? info.default);
          }
        }
        return next;
      });
      setMaxclientsSeeded((seeded) => {
        const mc = resp.rec_engine?.maxclients_current;
        if (!seeded && mc != null) {
          setMaxclientsInput(String(mc));
          return true;
        }
        return seeded;
      });
    } catch (err: any) {
      console.error(err);
    }
  }, []);

  const saveMaxclients = async () => {
    const value = parseInt(maxclientsInput, 10);
    if (!Number.isFinite(value) || value <= 0) {
      setMaxclientsError("Enter a positive number");
      return;
    }
    setMaxclientsSaving(true);
    setMaxclientsError("");
    try {
      const resp = await adminService.setRedisMaxclients(value);
      setMaxclientsInput(String(resp.current));
      await fetchPools();
    } catch (err: any) {
      setMaxclientsError(err?.response?.data?.detail || "Failed to update");
    } finally {
      setMaxclientsSaving(false);
    }
  };

  useEffect(() => {
    fetchConnections();
    fetchPools();
  }, [fetchConnections, fetchPools]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => {
      fetchConnections();
      fetchPools();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [autoRefresh, fetchConnections, fetchPools]);

  const savePoolSize = async (service: string) => {
    const size = parseInt(poolInputs[service] ?? "", 10);
    if (!Number.isFinite(size) || size <= 0) {
      setPoolErrors((prev) => ({ ...prev, [service]: "Enter a positive number" }));
      return;
    }
    setPoolSaving((prev) => ({ ...prev, [service]: true }));
    setPoolErrors((prev) => ({ ...prev, [service]: "" }));
    try {
      const resp = await adminService.setRedisPoolSizeFor(service, size);
      setPoolInputs((prev) => ({ ...prev, [service]: String(resp.current) }));
      await fetchPools();
    } catch (err: any) {
      setPoolErrors((prev) => ({ ...prev, [service]: err?.response?.data?.detail || "Failed to update" }));
    } finally {
      setPoolSaving((prev) => ({ ...prev, [service]: false }));
    }
  };

  const TABS: { id: Tab; label: string; hint: string }[] = [
    { id: "connections", label: "Connections", hint: "Users holding a live socket + pool config" },
    { id: "realtime", label: "Realtime workers", hint: "Per-worker sockets and fan-out health" },
    { id: "redis", label: "Redis", hint: "Memory, connections, namespaces, TTL risk" },
    { id: "queues", label: "Queues", hint: "Durable stream depth and consumer lag" },
  ];

  return (
    <div>
      <div className="mb-5 flex flex-wrap gap-1 border-b border-white/10">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            title={t.hint}
            className={`px-3 py-2 text-sm font-medium transition-colors ${
              tab === t.id
                ? "border-b-2 border-indigo-400 text-indigo-300"
                : "text-slate-500 hover:text-slate-300"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "realtime" && <RealtimePanel />}
      {tab === "redis" && <RedisPanel />}
      {tab === "queues" && <QueuePanel />}

      <div className={tab === "connections" ? "" : "hidden"}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 mb-6">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-white">WebSocket Connection Health</h2>
          <p className="text-sm text-gray-500 mt-1">
            Users currently holding a live WebSocket connection, across all backend processes.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 sm:shrink-0 sm:justify-end">
          {lastUpdated && (
            <span className="text-xs text-gray-500 whitespace-nowrap">
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="accent-indigo-500"
            />
            Auto-refresh
          </label>
          <RefreshButton
            onRefresh={async () => { await Promise.all([fetchConnections(), fetchPools()]); }}
            label="Refresh now"
            className="px-3 py-2 bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20"
          />
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <p className="text-gray-500 text-sm">Live connections</p>
          <p className="text-3xl font-bold text-white mt-1">{data?.total_connections ?? "—"}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <p className="text-gray-500 text-sm">Redis presence backend</p>
          <p className="text-3xl font-bold text-white mt-1">
            {loading ? "—" : data?.redis_enabled ? "Enabled" : "In-memory only"}
          </p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <p className="text-gray-500 text-sm">Total sockets</p>
          <p className="text-3xl font-bold text-white mt-1">
            {data?.connections.reduce((sum, c) => sum + c.socket_count, 0) ?? "—"}
          </p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <p className="text-gray-500 text-sm">Redis client connections</p>
          {/* Redis Cloud / managed instances often cap total client
              connections well below what this process's own pool could open
              — this is the number that actually explains dropped DM sockets
              when it's near the cap. */}
          <p
            className={`text-3xl font-bold mt-1 ${
              data?.redis_clients_connected != null &&
              data?.redis_clients_max != null &&
              data.redis_clients_connected / data.redis_clients_max > 0.8
                ? "text-amber-400"
                : "text-white"
            }`}
          >
            {loading
              ? "—"
              : data?.redis_clients_connected == null
                ? "N/A"
                : data?.redis_clients_max != null
                  ? `${data.redis_clients_connected} / ${data.redis_clients_max}`
                  : data.redis_clients_connected}
          </p>
        </div>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden mb-6">
        <div className="px-4 py-3 border-b border-gray-800 space-y-2">
          <p className="text-sm font-medium text-white">How to read this before changing anything</p>
          <p className="text-xs text-gray-500">
            <span className="text-gray-300 font-medium">Which Redis does each row hit? </span>
            <span className="text-white font-medium">backend_cache, rag_inventory_catalog, rag_embedding,
            s3_presign</span> all share <span className="text-white font-medium">one external Redis</span> used
            by the backend + RAG + OCR services. That instance's own connection ceiling is fixed by its
            hosting plan — this page can only ask it to open <em>fewer</em> connections, it cannot raise
            what the provider allows. <span className="text-white font-medium">rec_engine</span> is different:
            it runs on its own separate, dedicated Redis, which is why it's the only row with its own
            "server maxclients cap" control further down this page — that setting affects rec_engine only,
            never the shared instance the other four rows use.
          </p>
          <p className="text-xs text-gray-500">
            <span className="text-gray-300 font-medium">The math before you raise a number: </span>
            real connection demand = <span className="text-white">(worker processes running this service) ×
            (the pool size below)</span> — not per user, per worker. A service running 4 worker processes
            with a pool of 50 opens <span className="text-white">200</span> connections, not 50; check
            GUNICORN_WORKERS / UVICORN_WORKERS on that service's deployment to know your multiplier.
            Compare that total against the "Redis client connections" cap in the tiles above — that's the
            hard ceiling every pool on that Redis shares.
          </p>
          <p className="text-xs text-gray-500">
            <span className="text-gray-300 font-medium">What a bigger pool actually buys you: </span>
            each open connection can typically run roughly 500–1,000 Redis commands/sec (a round trip is
            about 1–2ms), so <span className="text-white">total throughput ≈ connections × ~500–1,000
            ops/sec</span> — a rough ceiling, not a guarantee. On a managed/cloud plan, the provider also
            enforces its <span className="text-white">own flat ops/sec limit</span> (e.g. a plan advertised
            as "1,000 ops/sec") that applies across every connection combined — raising the pool size here
            cannot push traffic past that number, it can only get you throttled sooner if you're already
            near it.
          </p>
          <p className="text-xs text-gray-500 pt-1 border-t border-gray-800/60">
            Changes apply live (no restart) and persist. "Connected" means this process currently has that
            pool open; a service showing "not yet connected" just hasn't been used since the last restart
            — it'll pick up its saved size the moment it does.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Service</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Status</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Default</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Current live cap</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Set new size</th>
              </tr>
            </thead>
            <tbody>
              {!pools ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-gray-500">
                    <Spinner />
                  </td>
                </tr>
              ) : (
                (Object.entries(pools) as [string, AllRedisPoolSizesResponse[string]][])
                  // "_summary" is grouped totals, not a pool row; "rec_api" is
                  // informational only (backend's small publish_event()-only
                  // pool) — neither is independently settable here.
                  .filter((entry): entry is [string, RedisPoolInfo] =>
                    entry[0] !== "_summary" && entry[0] !== "rec_api",
                  )
                  .map(([service, info]) => (
                  <tr key={service} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                    <td className="py-3 px-4 text-white">{info.label}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                          info.connected ? "bg-emerald-500/10 text-emerald-400" : "bg-gray-700/50 text-gray-400"
                        }`}
                      >
                        {info.connected ? "Connected" : "Not yet connected"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-400">{info.default}</td>
                    <td className="py-3 px-4 text-gray-400">{info.current ?? "—"}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={1}
                          value={poolInputs[service] ?? ""}
                          onChange={(e) => setPoolInputs((prev) => ({ ...prev, [service]: e.target.value }))}
                          className="w-24 px-2 py-1.5 text-sm bg-gray-950 border border-gray-800 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                        />
                        <button
                          onClick={() => savePoolSize(service)}
                          disabled={poolSaving[service]}
                          className="px-3 py-1.5 text-sm bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {poolSaving[service] ? "Saving…" : "Save"}
                        </button>
                        {poolErrors[service] && <span className="text-xs text-red-400">{poolErrors[service]}</span>}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pools?.rec_engine && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden mb-6">
          <div className="px-4 py-3 border-b border-gray-800">
            <p className="text-sm font-medium text-white">Rec engine Redis — server maxclients cap</p>
            <p className="text-xs text-gray-500 mt-0.5">
              This control applies to <span className="text-white font-medium">rec_engine's own dedicated
              Redis only</span> — it has no effect on backend, RAG, or OCR, which sit on a separate,
              externally-managed Redis this page cannot configure (see the note above the pool table).
            </p>
            <p className="text-xs text-gray-500 mt-1">
              How many connections the Redis SERVER will accept in total, from every process sharing it —
              not this app's own pool size (that's the "Rec engine" row in the table above). Real demand on
              this cap comes from TWO different processes: the dedicated rec-server's own pool, AND every
              backend API worker's smaller publish_event()-only pool — both land on the same instance.
              {pools._summary?.rec_dedicated && (
                <>
                  {" "}Right now that's an estimated{" "}
                  <span className="text-white font-medium">
                    {pools._summary.rec_dedicated.estimated_connections}
                  </span>{" "}
                  connections ({pools._summary.rec_dedicated.assumed_rec_workers} rec-server worker(s) +{" "}
                  {pools._summary.rec_dedicated.assumed_backend_workers} backend worker(s)).
                </>
              )}{" "}
              Keep this cap comfortably above that total — equal or close to it leaves no room for a deploy
              overlap or a debug `redis-cli` session before you hit "max number of clients reached." Default
              from env: {pools.rec_engine.maxclients_default ?? "—"}.
            </p>
          </div>
          <div className="px-4 py-3 flex items-center gap-3">
            <span className="text-sm text-gray-400">
              Current live cap: {pools.rec_engine.maxclients_current ?? "—"}
            </span>
            <input
              type="number"
              min={1}
              value={maxclientsInput}
              onChange={(e) => setMaxclientsInput(e.target.value)}
              className="w-28 px-2 py-1.5 text-sm bg-gray-950 border border-gray-800 rounded-lg text-white focus:outline-none focus:border-indigo-500"
            />
            <button
              onClick={saveMaxclients}
              disabled={maxclientsSaving}
              className="px-3 py-1.5 text-sm bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 rounded-lg transition-colors disabled:opacity-50"
            >
              {maxclientsSaving ? "Saving…" : "Save"}
            </button>
            {maxclientsError && <span className="text-xs text-red-400">{maxclientsError}</span>}
          </div>
        </div>
      )}

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden mb-6">
        <div className="px-4 py-3 border-b border-gray-800">
          <p className="text-sm font-medium text-white">Raw Redis connections</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Every TCP connection Redis itself has open right now, across every process sharing this
            URL (backend, rag, rec) — not just DM app users.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Client ID</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Address</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Service (DB)</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Name</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Age</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Idle</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">What it's doing (last cmd)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-20 text-center text-gray-500">
                    <Spinner />
                  </td>
                </tr>
              ) : !data || data.redis_raw_clients.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-gray-500">
                    {data?.redis_enabled ? "No raw Redis clients found" : "Redis is disabled"}
                  </td>
                </tr>
              ) : (
                data.redis_raw_clients.map((c) => (
                  <tr key={c.id} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                    <td className="py-3 px-4 text-gray-400">{c.id}</td>
                    <td className="py-3 px-4 text-gray-400">{c.addr || "—"}</td>
                    <td className="py-3 px-4 text-gray-400">
                      {c.service_guess ? (
                        <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-indigo-500/10 text-indigo-400">
                          {c.service_guess}
                        </span>
                      ) : (
                        "—"
                      )}
                      <span className="text-gray-600"> (db{c.db})</span>
                    </td>
                    <td className="py-3 px-4 text-gray-400">{c.name || "—"}</td>
                    <td className="py-3 px-4 text-gray-400">{c.age_sec != null ? `${c.age_sec}s` : "—"}</td>
                    <td className="py-3 px-4 text-gray-400">{c.idle_sec != null ? `${c.idle_sec}s` : "—"}</td>
                    <td className="py-3 px-4 text-gray-400">
                      <div>{c.cmd_explained || "—"}</div>
                      {c.cmd && <div className="text-[11px] font-mono text-gray-600 mt-0.5">{c.cmd}</div>}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-4 text-gray-500 font-medium">User</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Role</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Contact</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Sockets</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Redis confirmed</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="py-20 text-center text-gray-500">
                    <Spinner />
                  </td>
                </tr>
              ) : !data || data.connections.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-20 text-center text-gray-500">
                    No users currently connected
                  </td>
                </tr>
              ) : (
                data.connections.map((c) => (
                  <tr
                    key={c.user_id}
                    className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
                        <span className="text-white font-medium">{c.name || c.user_id}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-400 capitalize">{c.role || "—"}</td>
                    <td className="py-3 px-4 text-gray-400">
                      {c.email || "—"}
                      {c.phone && <div className="text-xs text-gray-500">{c.phone}</div>}
                    </td>
                    <td className="py-3 px-4 text-gray-400">{c.socket_count}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                          c.redis_confirmed
                            ? "bg-emerald-500/10 text-emerald-400"
                            : "bg-gray-700/50 text-gray-400"
                        }`}
                      >
                        {data.redis_enabled ? (c.redis_confirmed ? "Yes" : "No") : "N/A"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
  );
};

export default AdminWebSocketHealthPage;
